import React, {FC, useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showSuccessToast, showErrorToast} from '../../../../shared/components/ToastUtils'
import {
  IMenuModel,
  IMenuFormValues,
  IMenuReorderRequest,
  IMenuTreeModel,
  UNASSIGNED_TREE,
} from './MenuModel'
import {
  createMenu,
  updateMenu,
  deleteMenu,
  loadMenus,
  reorderMenus,
  loadMenuTrees,
  createMenuTree,
  updateMenuTree,
  deleteMenuTree,
} from './Api'
import MenuTree from './MenuTree'
import MenuFormModal from './MenuFormModal'
import {MenuTreeBar, MenuTreeFormModal} from './MenuTreeBar'

const apiErrorDescription = (error: any, fallback: string): string =>
  error?.response?.data?.friendlyMessage?.description || fallback

const MenuForm: FC = () => {
  const [initialLoading, setInitialLoading] = useState(true)
  const [saveLoading, setSaveLoading] = useState(false)
  const [menuTree, setMenuTree] = useState<IMenuModel[]>([])

  // Menü ağaçları (yazılımlar); menüler seçili ağaca göre listelenir
  const [trees, setTrees] = useState<IMenuTreeModel[]>([])
  const [selectedTreeId, setSelectedTreeId] = useState<string>('')
  const [showTreeModal, setShowTreeModal] = useState(false)
  const [editingTree, setEditingTree] = useState<IMenuTreeModel | null>(null)
  const [deletingTree, setDeletingTree] = useState<IMenuTreeModel | null>(null)
  const [treeSaving, setTreeSaving] = useState(false)

  // Modal state
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingItem, setEditingItem] = useState<IMenuModel | null>(null)
  const [parentItem, setParentItem] = useState<IMenuModel | null>(null)

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingItem, setDeletingItem] = useState<IMenuModel | null>(null)

  const fetchTrees = useCallback(async (selectId?: string) => {
    try {
      const response = await loadMenuTrees()
      const items = response.data?.payload ?? []
      setTrees(items)
      setSelectedTreeId((current) => {
        if (selectId) return selectId
        if (current && (current === UNASSIGNED_TREE || items.some((tree) => tree.id === current))) return current
        return items[0]?.id ?? UNASSIGNED_TREE
      })
    } catch (error) {
      showErrorToast('Menü ağaçları yüklenirken bir hata oluştu.')
      setSelectedTreeId((current) => current || UNASSIGNED_TREE)
    }
  }, [])

  useEffect(() => {
    fetchTrees()
  }, [fetchTrees])

  // Seçili ağacın menülerini yükle
  const fetchMenus = useCallback(async () => {
    if (!selectedTreeId) return
    try {
      const response = await loadMenus(selectedTreeId)
      const payload: any = response.data?.payload
      const items: IMenuModel[] = Array.isArray(payload) ? payload : payload?.content ?? []
      setMenuTree(items)
    } catch (error) {
      console.error('Menüler yüklenirken hata:', error)
      showErrorToast('Menüler yüklenirken bir hata oluştu.')
    } finally {
      setInitialLoading(false)
    }
  }, [selectedTreeId])

  useEffect(() => {
    fetchMenus()
  }, [fetchMenus])

  const getParentOptions = useCallback((): {value: string; label: string}[] => {
    const result: {value: string; label: string}[] = []
    const flatten = (nodes: IMenuModel[], depth: number) => {
      nodes.forEach((node) => {
        if (node.id !== editingItem?.id) {
          result.push({value: node.id!, label: `${'— '.repeat(depth)}${node.title}`})
        }
        if (node.children && node.children.length > 0) {
          flatten(node.children, depth + 1)
        }
      })
    }
    flatten(menuTree, 0)
    return result
  }, [menuTree, editingItem])

  // Yeni menü ekleme
  const handleAddNew = () => {
    setEditingItem(null)
    setParentItem(null)
    setShowFormModal(true)
  }

  // Alt menü ekleme
  const handleAddChild = (parent: IMenuModel) => {
    setEditingItem(null)
    setParentItem(parent)
    setShowFormModal(true)
  }

  // Düzenleme
  const handleEdit = (item: IMenuModel) => {
    setEditingItem(item)
    setParentItem(null)
    setShowFormModal(true)
  }

  // Silme onayı
  const handleDeleteConfirm = (item: IMenuModel) => {
    setDeletingItem(item)
    setShowDeleteModal(true)
  }

  // Silme işlemi
  const handleDelete = useCallback(async () => {
    if (!deletingItem?.id) return

    try {
      await deleteMenu(deletingItem.id)
      showSuccessToast('Menü başarıyla silindi.')
      await fetchMenus()
      fetchTrees()
    } catch (error) {
      console.error('Menü silinirken hata:', error)
      showErrorToast('Menü silinirken bir hata oluştu.')
    } finally {
      setShowDeleteModal(false)
      setDeletingItem(null)
    }
  }, [deletingItem, fetchMenus, fetchTrees])

  // Form gönderimi (oluşturma / güncelleme)
  const handleFormSubmit = async (values: IMenuFormValues) => {
    setSaveLoading(true)
    try {
      if (values.id) {
        const existing = editingItem
        await updateMenu(values.id, {
          title: values.title,
          icon: values.icon,
          path: values.path,
          parentId: values.parentId,
          orderIndex: existing?.orderIndex ?? 0,
          isActive: values.isActive,
          componentName: values.componentName || null,
          menuTreeId: values.menuTreeId,
        })
        showSuccessToast('Menü başarıyla güncellendi.')
      } else {
        await createMenu({
          title: values.title,
          icon: values.icon,
          path: values.path,
          parentId: values.parentId,
          orderIndex: 0,
          isActive: values.isActive,
          componentName: values.componentName || null,
          menuTreeId: values.menuTreeId,
        })
        showSuccessToast('Menü başarıyla oluşturuldu.')
      }

      setShowFormModal(false)
      setEditingItem(null)
      setParentItem(null)
      await fetchMenus()
      fetchTrees()
    } catch (error) {
      console.error('Menü kaydedilirken hata:', error)
      showErrorToast('Menü kaydedilirken bir hata oluştu.')
    } finally {
      setSaveLoading(false)
    }
  }

  const handleTreeSubmit = async (values: {name: string; description: string}) => {
    setTreeSaving(true)
    try {
      if (editingTree) {
        await updateMenuTree(editingTree.id, values)
        showSuccessToast('Menü ağacı güncellendi.')
        await fetchTrees()
      } else {
        const response = await createMenuTree(values)
        showSuccessToast('Menü ağacı oluşturuldu. Menülerini bu ağacın altına ekleyebilirsiniz.')
        await fetchTrees(response.data.payload.id)
      }
      setShowTreeModal(false)
      setEditingTree(null)
    } catch (error) {
      showErrorToast(apiErrorDescription(error, 'Menü ağacı kaydedilemedi.'))
    } finally {
      setTreeSaving(false)
    }
  }

  const handleTreeDelete = async () => {
    if (!deletingTree) return
    try {
      await deleteMenuTree(deletingTree.id)
      showSuccessToast('Menü ağacı silindi.')
      setSelectedTreeId('')
      await fetchTrees()
    } catch (error) {
      showErrorToast(apiErrorDescription(error, 'Menü ağacı silinemedi.'))
    } finally {
      setDeletingTree(null)
    }
  }

  // Sıralama değişikliği (drag & drop)
  const handleReorder = async (reorderItems: IMenuReorderRequest[]) => {
    try {
      await reorderMenus(reorderItems)
      showSuccessToast('Menü sıralaması güncellendi.')
      await fetchMenus()
    } catch (error) {
      console.error('Sıralama güncellenirken hata:', error)
      showErrorToast('Sıralama güncellenirken bir hata oluştu.')
      await fetchMenus() // Hata durumunda eski sıralamayı geri yükle
    }
  }

  return (
    <>
      <ToastContainer />
      {/* Üst başlık ve aksiyonlar */}
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Menü Yönetimi</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Her yazılımın menüleri kendi menü ağacının altında tanımlanır. Menüleri sürükleyerek sıralayabilir, alt
              menü ekleyebilirsiniz.
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={handleAddNew}
            >
              <i className='bi bi-plus-lg me-1' />
              Yeni Menü Ekle
            </button>
          </div>
        </div>

        <div className='card-body py-3'>
          <div className='mb-6'>
            <MenuTreeBar
              trees={trees}
              selectedTreeId={selectedTreeId}
              onSelect={(treeId) => {
                setInitialLoading(true)
                setSelectedTreeId(treeId)
              }}
              onCreate={() => {
                setEditingTree(null)
                setShowTreeModal(true)
              }}
              onEdit={(tree) => {
                setEditingTree(tree)
                setShowTreeModal(true)
              }}
              onDelete={setDeletingTree}
            />
            {selectedTreeId === UNASSIGNED_TREE && (
              <div className='alert alert-warning mt-4 mb-0 py-3'>
                Bu menüler henüz bir menü ağacına bağlı değil. Kök menüyü düzenleyip ağaç seçtiğinizde alt menüleriyle
                birlikte o ağaca taşınır.
              </div>
            )}
          </div>
          {initialLoading ? (
            <div className='d-flex justify-content-center py-10'>
              <span className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Yükleniyor...</span>
              </span>
            </div>
          ) : (
            <MenuTree
              tree={menuTree}
              onEdit={handleEdit}
              onDelete={handleDeleteConfirm}
              onAddChild={handleAddChild}
              onReorder={handleReorder}
            />
          )}
        </div>
      </div>

      {/* Form Modal */}
      <MenuFormModal
        show={showFormModal}
        editingItem={editingItem}
        parentItem={parentItem}
        parentOptions={getParentOptions()}
        treeOptions={trees.map((tree) => ({value: tree.id, label: tree.name}))}
        defaultTreeId={selectedTreeId === UNASSIGNED_TREE ? null : selectedTreeId || null}
        loading={saveLoading}
        onClose={() => {
          setShowFormModal(false)
          setEditingItem(null)
          setParentItem(null)
        }}
        onSubmit={handleFormSubmit}
      />

      {/* Silme Onay Modal */}
      <ConfirmModal
        show={showDeleteModal}
        header='Menü Silme'
        text={
          deletingItem?.children && deletingItem.children.length > 0
            ? `"${deletingItem?.title}" menüsü ve alt menüleri silinecektir. Emin misiniz?`
            : `"${deletingItem?.title}" menüsünü silmek istediğinize emin misiniz?`
        }
        handleClose={() => {
          setShowDeleteModal(false)
          setDeletingItem(null)
        }}
        applyOnClick={handleDelete}
      />

      <MenuTreeFormModal
        show={showTreeModal}
        editingTree={editingTree}
        loading={treeSaving}
        onClose={() => {
          setShowTreeModal(false)
          setEditingTree(null)
        }}
        onSubmit={handleTreeSubmit}
      />

      <ConfirmModal
        show={!!deletingTree}
        header='Menü Ağacını Sil'
        text={
          deletingTree && deletingTree.menuCount > 0
            ? `"${deletingTree.name}" ağacında ${deletingTree.menuCount} menü var; önce menüleri silmeli ya da başka ağaca taşımalısınız.`
            : `"${deletingTree?.name ?? ''}" menü ağacını silmek istediğinize emin misiniz?`
        }
        handleClose={() => setDeletingTree(null)}
        applyOnClick={handleTreeDelete}
      />

      {/* Stil */}
      <style>{`
        .menu-tree-item:hover {
          background-color: #f5f8fa;
        }
        .cursor-grab {
          cursor: grab !important;
        }
        .cursor-grab:active {
          cursor: grabbing !important;
        }
      `}</style>
    </>
  )
}

const MenuPage: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Menü Yönetimi</PageTitle>
      <MenuForm />
    </>
  )
}

export {MenuPage}
